package music.jammers.client.shared.event.presentation.viewmodel

import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import music.jammers.client.shared.analytics.event.EventAnalytics
import music.jammers.client.shared.component.BaseViewModel
import music.jammers.client.shared.component.customExceptionHandler
import music.jammers.client.shared.component.update
import music.jammers.client.shared.event.domain.model.ValidationError
import music.jammers.client.shared.event.domain.model.ValidationErrorValue
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase
import music.jammers.client.shared.event.domain.validator.EventFieldValidator
import music.jammers.shared.event.domain.model.CardInfo
import music.jammers.shared.event.domain.model.EventData
import music.jammers.shared.event.domain.model.EventInstrument
import music.jammers.shared.instrument.domain.model.InstrumentType
import music.limasoul.client.shared.event.domain.usecase.DeleteEventUseCase
import music.limasoul.client.shared.event.domain.usecase.GetEventUseCase
import music.limasoul.client.shared.event.domain.usecase.UpdateEventUseCase
import music.limasoul.client.shared.navigation.Router

class EditEventViewModel(
    private val validateEventUseCase: ValidateEventUseCase,
    private val getEventUseCase: GetEventUseCase,
    private val updateEventUseCase: UpdateEventUseCase,
    private val deleteEventUseCase: DeleteEventUseCase,
    private val eventAnalytics: EventAnalytics,
    private val eventFieldValidator: EventFieldValidator,
    private val router: Router,
    initState: EditEventState = EditEventState(progress = true),
) : BaseViewModel() {

    internal val state: State<EditEventState> get() = _state
    internal val validationState: State<EditEventValidationState> get() = _validationState

    private val _state = mutableStateOf(initState)
    private val _validationState = mutableStateOf(EditEventValidationState())
    private val digitRegex = "^\\d+$".toRegex()

    internal fun onReady(eventId: Long) {
        _state.update {
            copy(
                eventId = eventId,
            )
        }

        launch(
            customExceptionHandler(router) { _, throwable ->
                _state.update {
                    copy(
                        progress = false,
                        error = throwable,
                    )
                }
            }
        ) {
            val event = getEventUseCase(eventId)

            _state.update {
                copy(
                    progress = false,
                    title = event.title,
                    description = event.description,
                    cardInfo = event.cardInfo,
                    date = event.date,
                    genre = event.genre,
                    spot = event.spot,
                    songsCount = event.songsLimit.toString(),
                    appliesPerPerson = event.appliesPerPerson.toString(),
                    selectedInstruments = event.eventInstruments,
                )
            }
        }
    }

    internal fun onTitleChanged(value: String) {
        _state.update {
            copy(
                title = value,
            )
        }

        validateField(ValidateEventUseCase.TITLE, value)
    }

    internal fun onDescriptionChanged(value: String) {
        _state.update {
            copy(
                description = value,
            )
        }

        validateField(ValidateEventUseCase.DESCRIPTION, value)
    }

    internal fun onCardInfoChanged(value: CardInfo) {
        _state.update {
            copy(
                cardInfo = value,
            )
        }

        validateCardInfo(value)
    }

    internal fun onDateChanged(value: LocalDateTime) {
        _state.update {
            copy(
                date = value,
            )
        }

        validateField(ValidateEventUseCase.DATE, value.toString())
    }

    internal fun onGenreChanged(value: String) {
        _state.update {
            copy(
                genre = value,
            )
        }

        validateField(ValidateEventUseCase.GENRE, value)
    }

    internal fun onSpotChanged(value: String) {
        _state.update {
            copy(
                spot = value,
            )
        }

        validateField(ValidateEventUseCase.SPOT, value)
    }

    internal fun onSongsCountChanged(value: String) {
        if (value.isEmpty() || digitRegex.matches(value)) {
            _state.update {
                copy(
                    songsCount = value,
                )
            }

            validateField(ValidateEventUseCase.SONGS_COUNT, value)
        }
    }

    internal fun onAppliesChanged(value: String) {
        if (value.isEmpty() || digitRegex.matches(value)) {
            _state.update {
                copy(
                    appliesPerPerson = value,
                )
            }

            validateField(ValidateEventUseCase.APPLIES_PER_PERSON, value)
        }
    }

    internal fun onBackPressed() {
        router.back()
    }

    fun onDeleteEventCancelClicked() {
        _state.update {
            copy(
                deleteEventDialogOpened = false,
            )
        }
    }

    fun onDeleteEventDialogOkPressed() {
        _state.update {
            copy(
                deleteEventDialogOpened = false,
                progress = true,
            )
        }

        launch(
            customExceptionHandler(router) { _, throwable ->
                eventAnalytics.onError(throwable)
                _state.update {
                    copy(
                        error = throwable,
                        progress = false,
                    )
                }
            }
        ) {
            deleteEventUseCase(state.value.eventId)
            router.navigateToHome()
        }
    }

    internal fun onInstrumentDeleted(index: Int) {
        val selected = state.value.selectedInstruments.toMutableList()
        selected.removeAt(index)

        _state.update {
            copy(selectedInstruments = selected)
        }

        validateInstruments(selected)
    }

    internal fun onInstrumentAddClicked() {
        _state.update {
            copy(
                bottomNavigationOpen = true,
            )
        }
    }

    internal fun onInstrumentClicked(eventInstrument: EventInstrument) {
        val newStatus = when (eventInstrument.status) {
            EventInstrument.Status.REQUIRED -> EventInstrument.Status.OPTIONAL
            EventInstrument.Status.OPTIONAL -> EventInstrument.Status.REQUIRED
        }

        _state.update {
            val list = _state.value.selectedInstruments.toMutableList()
            val index = list.indexOf(eventInstrument)

            list.removeAt(index)
            list.add(
                index,
                eventInstrument.copy(
                    status = newStatus,
                ),
            )

            copy(
                selectedInstruments = list
            )
        }
    }

    internal fun onInstrumentAdded(instrumentType: InstrumentType) {
        val selected = state.value.selectedInstruments.toMutableList()
        selected.add(EventInstrument(instrumentType, EventInstrument.Status.REQUIRED))

        _state.update {
            copy(
                selectedInstruments = selected,
                bottomNavigationOpen = false,
            )
        }

        validateInstruments(selected)
    }

    internal fun onSaveClicked() {
        val stateValue = state.value

        val eventData = EventData(
            title = stateValue.title,
            description = stateValue.description,
            date = stateValue.date ?: Clock.System.now()
                .toLocalDateTime(TimeZone.currentSystemDefault()),
            genre = stateValue.genre,
            spot = stateValue.spot,
            songsCount = stateValue.songsCount.toIntOrNull() ?: 0,
            appliesPerPerson = stateValue.appliesPerPerson.toIntOrNull() ?: 0,
            selectedInstrumentList = stateValue.selectedInstruments,
            cardInfo = stateValue.cardInfo ?: CardInfo.INVALID
        )

        val errors = validateEventUseCase(eventData)

        if (errors.isEmpty()) {
            updateEvent(eventData)
        } else {
            _validationState.update {
                copy(
                    validationErrorMap = errors.associateBy { item ->
                        item.fieldName
                    }
                )
            }
        }
    }

    private fun updateEvent(eventDataModel: EventData) {
        _state.update {
            copy(
                progress = true
            )
        }

        launch(
            customExceptionHandler(router) { _, throwable ->
                _state.update {
                    copy(
                        error = throwable
                    )
                }
            }
        ) {
            updateEventUseCase.invoke(state.value.eventId, eventDataModel)
            router.back()
        }
    }

    internal fun onBottomSheetClosed() {
        _state.update {
            copy(
                bottomNavigationOpen = false
            )
        }
    }

    internal fun onErrorClosed() {
        _state.update {
            copy(
                error = null,
            )
        }
        router.back()
    }

    internal fun onDeleteEventClicked() {
        _state.update {
            copy(
                deleteEventDialogOpened = true,
            )
        }
    }

    private fun validateField(name: String, value: String) {
        _validationState.update {
            val error = eventFieldValidator.validate(name, value)
            if (error == null) {
                resetError(name)
            } else {
                addError(error)
            }
        }
    }

    private fun validateCardInfo(value: CardInfo) {
        _validationState.update {
            if (value == CardInfo.INVALID) {
                addError(
                    ValidationError(
                        ValidateEventUseCase.CARD_INFO,
                        ValidationErrorValue.EmptyValue,
                    )
                )
            } else {
                resetError(ValidateEventUseCase.CARD_INFO)
            }
        }
    }

    private fun validateInstruments(selected: List<EventInstrument>) {
        _validationState.update {
            if (selected.isEmpty()) {
                addError(
                    ValidationError(
                        ValidateEventUseCase.SELECTED_INSTRUMENTS,
                        ValidationErrorValue.ItemNotSelected,
                    )
                )
            } else {
                resetError(ValidateEventUseCase.SELECTED_INSTRUMENTS)
            }
        }
    }
}